import React, { useCallback, useContext, useMemo } from 'react';
import RadioInput from '../../../Inputs/RadioInput/RadioInput';
import Tooltip from '../../../Tips/Tooltip/Tooltip';
import { Column } from '../Layout/RecordCompareLayout';
import { getColumnId, getRowId } from '../../util/recordCompareAria';
import { selectAllDisabled } from '../../util/recordCompareSelect';
import RecordCompareStateContext from '../../RecordCompareStateContext';
import isSelectableAsMaster from '../../util/recordCompareIsSelectableAsMaster';
import styles from '../RecordObject/RecordComparePropertyTitle.module.scss';
import masterStyles from './RecordCompareMaster.module.scss';
import { injectIntl } from 'react-intl';
import MetaData from '../MetaData/RecordCompareMetaData';
import I18n from '../../../I18n/I18n/I18n';
var ARROW_RIGHT = 'ArrowRight';
var ARROW_LEFT = 'ArrowLeft';
var ARROW_DOWN = 'ArrowDown';
var ARROW_UP = 'ArrowUp';
function MasterColumn(_ref) {
  var index = _ref.index,
    recordId = _ref.recordId,
    intl = _ref.intl;
  var _useContext = useContext(RecordCompareStateContext),
    master = _useContext.master,
    recordIds = _useContext.recordIds,
    recordSecurities = _useContext.recordSecurities,
    records = _useContext.records,
    cardView = _useContext.cardView,
    mergeSelections = _useContext.mergeSelections,
    setMaster = _useContext.setMaster,
    showErrors = _useContext.showErrors;
  var recordSecurity = recordSecurities[index];
  var i18n = useMemo(function () {
    if (cardView) {
      return {
        id: '@dm/components.record-compare.labels.detail',
        values: {
          index: index + 1
        }
      };
    }
    return {
      id: '@dm/components.record-compare.labels.master-column',
      values: {
        index: index + 1
      }
    };
  }, [index]);
  var isSelected = useMemo(function () {
    return master === index;
  }, [master, index]);
  var handleChange = useCallback(function () {
    if (isSelected) {
      setMaster(null);
      mergeSelections(selectAllDisabled(null, records[0]));
    } else {
      setMaster(index);
      mergeSelections(selectAllDisabled(index, records[0]));
    }
  }, [mergeSelections, index, isSelected, master, setMaster]);
  var isSAM = useMemo(function () {
    return isSelectableAsMaster(recordIds, recordSecurities, index);
  }, [recordIds, recordSecurities, index]);
  var property = '_master';
  var checked = master === index;
  var hasSelection = useMemo(function () {
    return master !== null && master !== undefined;
  }, [master]);
  var handleKeyDown = useCallback(function (event) {
    if (event.key === ARROW_RIGHT || event.key === ARROW_UP) {
      event.stopPropagation();
      event.preventDefault();
      var radioButtons = document.querySelectorAll("input[name=\"".concat(property, "\"]"));
      if (index < radioButtons.length - 1) {
        radioButtons.item(index + 1).focus();
      } else {
        radioButtons.item(0).focus();
      }
    } else if (event.key === ARROW_LEFT || event.key === ARROW_DOWN) {
      event.stopPropagation();
      event.preventDefault();
      var _radioButtons = document.querySelectorAll("input[name=\"".concat(property, "\"]"));
      if (index !== 0) {
        _radioButtons.item(index - 1).focus();
      } else {
        _radioButtons.item(_radioButtons.length - 1).focus();
      }
    }
  }, [property, index]);
  return /*#__PURE__*/React.createElement(Column, {
    highlight: checked
  }, cardView ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: masterStyles.card
  }, /*#__PURE__*/React.createElement("span", {
    className: "".concat(styles.title, " ").concat(styles['card-view'], " ").concat(styles.required)
  }, /*#__PURE__*/React.createElement("input", {
    disabled: !checked && !isSAM,
    id: "".concat(recordId, "-").concat(property, "-").concat(index),
    type: "radio",
    checked: checked,
    name: "".concat(property),
    value: intl.formatMessage(i18n),
    onChange: handleChange,
    className: styles.radio,
    "aria-disabled": "false",
    "aria-describedby": "".concat(getColumnId(index), " ").concat(getRowId(property)),
    "aria-invalid": "false",
    "aria-labelledby": "".concat(recordId, "-").concat(property, "-").concat(index, "-label ").concat(recordId, "-").concat(property, "-").concat(index, "-description"),
    onKeyDown: handleKeyDown,
    "aria-required": true
  }), /*#__PURE__*/React.createElement("label", {
    id: "".concat(recordId, "-").concat(property, "-").concat(index, "-label"),
    htmlFor: "".concat(recordId, "-").concat(property, "-").concat(index),
    className: styles.label
  }, intl.formatMessage(i18n), /*#__PURE__*/React.createElement("span", {
    className: styles.required
  }), showErrors && !hasSelection ? /*#__PURE__*/React.createElement("span", {
    className: styles.error
  }) : null)), /*#__PURE__*/React.createElement("div", {
    className: masterStyles.description,
    id: "".concat(recordId, "-").concat(property, "-").concat(index, "-description")
  }, /*#__PURE__*/React.createElement(MetaData, {
    record: records[index],
    index: index
  }), showErrors && !hasSelection ? /*#__PURE__*/React.createElement("div", {
    className: masterStyles.errorText,
    "aria-atomic": true,
    "aria-live": "polite"
  }, /*#__PURE__*/React.createElement(I18n, {
    i18n: intl.formatMessage({
      id: '@dm/components.record-compare.labels.error'
    }, {
      type: intl.formatMessage(i18n)
    })
  })) : null))) : /*#__PURE__*/React.createElement(Tooltip, {
    i18nKey: "@dm/components.record-compare.tooltips.merge-disabled-record",
    neverShow: isSelectableAsMaster && recordSecurity.deletable,
    direction: "left"
  }, function (_ref2) {
    var ref = _ref2.ref,
      describedby = _ref2.describedby,
      events = _ref2.events;
    return /*#__PURE__*/React.createElement("span", Object.assign({
      "aria-describedby": describedby,
      ref: ref
    }, events), /*#__PURE__*/React.createElement(RadioInput, {
      "aria-describedby": "".concat(getColumnId(index), " ").concat(getRowId(property)),
      disabled: !checked && !isSAM,
      checked: checked,
      id: "".concat(recordId, "-").concat(property, "-").concat(index),
      name: "".concat(property),
      onChange: handleChange,
      size: "small",
      label: i18n
    }));
  }));
}
export default injectIntl(MasterColumn);